class IndexedDBManager {
    private dbName: string;
    private dbVersion: number;
    private db: IDBDatabase | null = null;

    constructor(dbName: string, dbVersion: number = 1) {
        this.dbName = dbName;
        this.dbVersion = dbVersion;
    }

    public async open(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
                this.db = request.result;
                if (!this.db.objectStoreNames.contains('items')) {
                    this.db.createObjectStore('items', { keyPath: 'id', autoIncrement: true });
                }
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    public async addItem(item: any): Promise<number> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error("Database is not open"));
                return;
            }
            const transaction = this.db.transaction('items', 'readwrite');
            const store = transaction.objectStore('items');
            const request = store.add(item);


            request.onsuccess = (event: any) => {
                const generatedId = event.target.result as number;
                resolve(generatedId);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    public async getItem(id: number): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error("Database is not open"));
                return;
            }
            const transaction = this.db.transaction('items', 'readonly');
            const store = transaction.objectStore('items');
            const request = store.get(id);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    public async getAllItems(): Promise<any[]> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error("Database is not open"));
                return;
            }
            const transaction = this.db.transaction('items', 'readonly');
            const store = transaction.objectStore('items');
            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    public async deleteItem(id: number): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error("Database is not open"));
                return;
            }
            const transaction = this.db.transaction('items', 'readwrite');
            const store = transaction.objectStore('items');
            const request = store.delete(id);

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    public async clearStore(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error("Database is not open"));
                return;
            }
            const transaction = this.db.transaction('items', 'readwrite');
            const store = transaction.objectStore('items');
            const request = store.clear();

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    public async updateItem(id: number, item: any): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error("Database is not open"));
                return;
            }
            const transaction = this.db.transaction('items', 'readwrite');
            const store = transaction.objectStore('items');
    
            let itemFound = false;
    
            transaction.oncomplete = () => {
                if (!itemFound) {
                    reject(new Error("Item not found"));
                }
            };
    
            transaction.onerror = () => {
                reject(transaction.error);
            };
    
            store.openCursor().onsuccess = (event: any) => {
                const cursor = event.target.result;
    
                if (cursor) {
                    if (cursor.value.id === id) {
                        itemFound = true;
                        const updatedItem = { 
                            ...cursor.value, 
                            item,
                            updatedAt: new Date().toISOString(),
                        };

                        const request = cursor.update(updatedItem);
                        request.onsuccess = () => {
                            resolve();
                        };
    
                        request.onerror = () => {
                            reject(request.error);
                        };
                    } else {
                        cursor.continue();
                    }
                } else if (!itemFound) {
                    reject(new Error("Item not found"));
                }
            };
    
            store.openCursor().onerror = () => {
                reject(store.openCursor().error);
            };
        });
    }
}

export default IndexedDBManager;